import { type Dispatch, type MutableRefObject, type SetStateAction, useEffect } from 'react'
import type { DragCreateState } from '../types/drag-create-state'

/**
 * While a shape drag-create is live, follow Shift (1:1) and Alt/Option (draw from center) as
 * they are pressed and released mid-drag.
 */
export function useDragCreateModifierKeys({
  dragCreate,
  dragCreateRef,
  setDragCreate,
}: {
  dragCreate: DragCreateState | null
  dragCreateRef: MutableRefObject<DragCreateState | null>
  setDragCreate: Dispatch<SetStateAction<DragCreateState | null>>
}) {
  // Sync Shift and Alt key state during shape drag; Alt toggles center-origin with smooth transition
  useEffect(() => {
    if (!dragCreate) return

    const onKey = (e: KeyboardEvent) => {
      const drag = dragCreateRef.current

      if (!drag) return

      if (e.key === 'Shift' || e.key === 'ShiftLeft' || e.key === 'ShiftRight') {
        dragCreateRef.current = { ...drag, shiftKey: e.shiftKey }
        setDragCreate(dragCreateRef.current)
        return
      }

      // Alt (Windows) / Option (Mac): react to modifier change so both platforms work
      const nextAltKey = e.altKey

      if (drag.altKey !== nextAltKey) {
        e.preventDefault()
        const { startCanvasX: sx, startCanvasY: sy, currentCanvasX: cx, currentCanvasY: cy } = drag

        if (nextAltKey) {
          // Pressing Alt/Option: switch to center mode; current box center becomes new start
          dragCreateRef.current = {
            ...drag,
            startCanvasX: (sx + cx) / 2,
            startCanvasY: (sy + cy) / 2,
            altKey: true,
          }
        } else {
          // Releasing Alt/Option: switch to corner mode; corner opposite pointer becomes new start
          dragCreateRef.current = {
            ...drag,
            startCanvasX: 2 * sx - cx,
            startCanvasY: 2 * sy - cy,
            altKey: false,
          }
        }

        setDragCreate(dragCreateRef.current)
      }
    }

    window.addEventListener('keydown', onKey)
    window.addEventListener('keyup', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('keyup', onKey)
    }
  }, [dragCreate, dragCreateRef, setDragCreate])
}
